import '../../utils/event-retargeting.js'
import { setLangCountryContext } from '../../utils/locale-utils'
er('gb-', 'gn-')

import { GbdsElement, OnInit, prop } from '@gbds/core/src/ts/core'
import { debounce } from '@gbds/core/src/ts/utils'
import { HEIGHT_RENDER, INITIAL_RENDER, NAV_STICKY, NAV_UNSTICKY } from '../../events'

import { isShiftTab, isTabOnly } from '@gbds/core/src/ts/utils'
import { getFirstAndLastFocusableElement } from '@gbds/core/src/ts/utils/firstlast-focusable-elements'
import { parseString } from '../../utils/dom-string-parser'
import { FlyoutService } from '../../utils/flyout-service'
import { getNetStorageContentPath, NetStorageEnv } from '../../utils/netstorage-paths'
import { PortableExperiencesEvents } from '../../utils/portable-experiences'
import { GbpeMyaccount } from './myaccount-nav'

import '../global-footer'
import './flyout'

import '@gbds/core/src/ts/components/expander'
import '@gbds/core/src/ts/components/tab-nav'
import { throttle } from '@gbds/core/src/ts/utils'

import { isMediumViewport, isSmallViewport } from '@gbds/core/src/ts/utils/viewport'
import '@gbds/quantum/src/ts/components/core/button/button'
import '@gbds/quantum/src/ts/components/core/disclosure/index'
import '@gbds/quantum/src/ts/components/core/dynamic-text/dynamic-text'
import '@gbds/quantum/src/ts/components/core/regional-disclosure/regional-disclosure'
import '@gbds/quantum/src/ts/components/core/regional-price/regional-price'
import '@gbds/quantum/src/ts/components/core/responsive-image/responsive-image'
import '@gbds/quantum/src/ts/components/core/target-zone/target-zone'
import '@gbds/quantum/src/ts/components/core/wrapper/wrapper'
import { isRTL, isWebViewApp } from '../../../../../gbds-core/src/ts/utils/helper-utils'
import { Language, Languages } from '../../utils/language-service'
import { NavConfig } from './configs/base'

interface RenderEvent {
	element: HTMLElement
	height: number
}

interface AuthStateElement {
	el: HTMLElement
	initialDisplay: string
}

/**
 * @noInheritDoc
 */
export class GlobalNav extends GbdsElement implements OnInit {
	//
	// Properties
	//

	// Private

	// private readonly showWhenAuthAttribute = 'show-auth'
	// private readonly showWhenUnauthAttribute = 'show-unauth'
	private readonly _templateSuffix = '-global_nav.html'

	private _firstRender = true
	private _lastTop = window.pageYOffset || document.documentElement.scrollTop

	// private authElements: AuthStateElement[]
	// private unauthElements: AuthStateElement[]

	private _initiallySticky: boolean
	private _flyoutService: FlyoutService
	private _scrollUpBegin: number
	private _initialBodyHeightValue: string
	private _windowInnerWidth: number
	private _navFocusableElements: Array<HTMLElement>
	private _myAccountNav: GbpeMyaccount

	protected _styles: string
	private _printStyles = `
	@media print {
		gb-global-nav, gb-global-footer, .gb-overlay {
			display: none;
		}
	}`

	// Public

	/**
	 * Testing/dev content URL override.
	 * **Cannot be changed after initialization.**
	 *
	 * Looks like `_base_url_override` as an attribute.
	 *
	 * ***DO NOT SET THIS UNLESS INSTRUCTED TO BY THE GLOBAL NAV TEAM***.
	 */
	@prop()
	_baseUrlOverride: NetStorageEnv

	/**
	 * Disables the nav's default behavior of showing the nav after scrolling up a certain amount of pixels.
	 * Can be changed at any time.
	 */
	@prop('boolean')
	disableScrollUpShow: boolean

	/**
	 * Hides the nav. Can be changed at any time.
	 */
	@prop('boolean', '_toggleHide')
	hide: boolean

	/**
	 * The number of pixels needed to scroll up before showing the nav.
	 * Defaults to 150 if not set. Can be changed at any time.
	 */
	@prop('number')
	showScrollHeight: number

	/**
	 * Sets the nav to be sticky (staying at the top of the screen when scrolling).
	 * Can be changed at any time.
	 */
	@prop('boolean', '_toggleSticky')
	sticky: boolean

	/**
	 * Determines the content that is put into the nav markup.
	 * **Cannot be changed after initialization**.
	 *
	 * Formatted as: brand-market-language-variant
	 *
	 * @example "chevrolet-us-en-standard"
	 */
	@prop()
	variation: string

	/**
	 * Determines the motion point link visiblility
	 * Set to true for brandsites by default and hidden in authenticated state
	 */
	@prop()
	enableLanguageSelector: boolean

	/**
	 * Query selector value identifying the applications root so the navigation can tab to the main content. Defaults to 'main'
	 * **Cannot be changed after initialization**.
	 *
	 *
	 * @example "main", "#__root", or ".root"
	 */
	@prop()
	appContentSelector: string

	//
	// Constructor
	//

	constructor(
		brandStyles: string,
		private _defaultVariation: string,
		private _navConfig: NavConfig,
		private _handleLanguageLinksPath
	) {
		super()
		this._flyoutService = new FlyoutService(this)
		this._styles = brandStyles
	}

	//
	// Lifecycle
	//

	async onInit(): Promise<void> {
		if (!isWebViewApp()) {
			this._initiallySticky = this.sticky
			this._windowInnerWidth = window.innerWidth

			const childTemplates = this.getElementsByTagName('template')
			if (this.childElementCount !== 0 && this._firstRender && childTemplates.length > 0) {
				await this._renderStatic()
				this._markNavReady()
			} else if (this._firstRender) {
				await this._renderDynamic(this.variation ?? this._defaultVariation)
				this._markNavReady()
			}

			if (isRTL()) {
				this.style.setProperty('--rotate-stacked-button-arrow', 'rotate(180deg)') // will flip stacked button arrow icon.
				this.style.setProperty('--rotate-back-button-arrow', 'rotate(0deg)') // will flip back button arrow icon.
			}

			// Load external custom colors
			this._loadExternalColors()

			// Set language country context
			setLangCountryContext(this.shadowRoot)

			this._initScrollListener()
			await this._setupLanguageSelector()
			this._updateLogoStatClass() // TODO: Remove once Quantum fix is done next release.
			this._addAriaLabel()
			this._addNavRole()

			// Used for custom tab order on small/medium viewports
			this._navFocusableElements = Array.from(
				this.shadowRoot.querySelectorAll<HTMLElement>(
					'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
				)
			)
			this._navFocusableElements = this._navFocusableElements.filter(el => !el.hasAttribute('disabled'))
			this._myAccountNav = this.shadowRoot.querySelector('gb-myaccount-nav')
			this._addTabListeners()
			this._addMyAccountTabListener()
			this._addMainContentShiftTabListener()
			this._addNonComposedEventListener()
		} else {
			// No render path in webview — still clear light-DOM height shell (display:block).
			this._markNavReady()
		}

		// If you're here there's no need to do any rendering, reference, or event
		// listener setup, it's already saved from first initialization
	}

	//
	// Methods
	//

	private _addNonComposedEventListener() {
		this.shadowRoot.addEventListener('change', this._reDispatchNonComposedEvent)
	}

	private _reDispatchNonComposedEvent(e: Event) {
		const newEvent = new CustomEvent(e.type, {
			...e,
			composed: true,
		})

		newEvent.composedPath = () => e.composedPath()
		window.dispatchEvent(newEvent)
	}

	// Private

	/**
	 * Adds the Navigation role to the Global Nav if the role has not been defined
	 * For better Accessibility
	 * @private
	 */
	private _addNavRole() {
		if (this.getAttribute('role') === null) {
			this.setAttribute('role', 'navigation')
		}
	}

	/**
	 * Clears the light-DOM height preload shell (`gb-global-nav:not(.gb-gn-ready)`).
	 * Call only after first shadow render (or webview skip) so the CLS height reserve
	 * is not dropped at custom-element upgrade.
	 */
	private _markNavReady() {
		this.classList.add('gb-gn-ready')
	}

	/**
	 * Adding Aria Label to the global nav custom element
	 * @private
	 */
	private _addAriaLabel() {
		const ariaLabel = this.shadowRoot.querySelector('.gn-aria-label')
		if (ariaLabel && ariaLabel.getAttribute('data-aria-label') !== null) {
			this.setAttribute('aria-label', ariaLabel.getAttribute('data-aria-label'))
		}
	}

	/**
	 * Temporary fix for analytics that can be removed once markup is updated in Quantum.
	 */
	private _updateLogoStatClass() {
		const logo: HTMLElement[] = Array.from(this.shadowRoot.querySelectorAll('.gn-logo-container a'))

		logo.forEach(l => {
			l.classList.remove('stat-image-link')
			l.classList.add('stat-logo')
		})
	}

	private async _setupLanguageSelector(): Promise<void> {
		this._registerLanguages()
		if (this._handleLanguageLinksPath) {
			window.gbpe.languageService.attachLanguageLinkHandlers(this)
		}
		this._hideUsAuthenticatedLanguageLinks()
	}

	private _registerLanguages(): void {
		const languageLinkList: HTMLElement[] = Array.from(this.shadowRoot.querySelectorAll('a[mporgnav]'))

		//Grab the languages from markup, with the first one being the current page, defaulting the languageCode to the nav config language
		const languages: Languages = {}
		languages[this._navConfig.language.toUpperCase()] = {
			languageCode: this._navConfig.language,
			url: window.location.href,
			default: true,
		}
		window.gbpe.languageService.init(languages)

		//Add any language selector links to the language service
		languageLinkList.forEach(languageLink => {
			if (languageLink.hasAttribute('data-lang')) {
				const languageCode = languageLink.getAttribute('data-lang')
				const languageURL = languageLink.getAttribute('data-href')
					? languageLink.getAttribute('data-href')
					: languageLink.getAttribute('href')
				const lang: Language = { languageCode: languageCode, url: languageURL }
				window.gbpe.languageService.addLanguage(lang)
			}
		})
	}

	private _hideUsAuthenticatedLanguageLinks(): void {
		void window.gbpe.auth.loaded.then(() => {
			if (window.gbpe.auth.currentAuthState === 1 && this._navConfig?.region === 'us') {
				const languageLinkList: HTMLElement[] = Array.from(this.shadowRoot.querySelectorAll('a[mporgnav]'))
				languageLinkList.forEach(languageLink => {
					languageLink.style.display = 'none'
				})
			}
		})
	}

	private _initScrollListener() {
		this.showScrollHeight = this.showScrollHeight ?? 150
		this._lastTop = window.scrollY
		document.addEventListener('scroll', throttle(this._handleScroll.bind(this), 200))
	}

	private _handleScroll() {
		const newScrollTop = window.pageYOffset || document.documentElement.scrollTop

		if (newScrollTop < this._lastTop) {
			this._handleScrollUp(newScrollTop)
		} else {
			this._handleScrollDown()
		}

		this._lastTop = newScrollTop <= 0 ? 0 : newScrollTop
	}

	private _handleScrollUp(newScrollTop: number) {
		// eslint-disable-next-line no-constant-condition
		if (false) {
			if (!this._scrollUpBegin) this._scrollUpBegin = newScrollTop

			const delta = this._scrollUpBegin - newScrollTop

			if (delta >= this.showScrollHeight && !this.sticky && !this._initiallySticky) {
				this._initialBodyHeightValue = document.body.style.height
				document.body.style.height = 'initial'
				this.sticky = true
			}
		}
	}

	private _handleScrollDown() {
		this._scrollUpBegin = null

		// Checks to make sure nav isn't always sticky and check to ensure we don't unsticky while a flyout is open or opening.
		if (!this._initiallySticky && !this.flyoutService.flyoutOpen && !this.flyoutService.flyoutOpening) {
			document.body.style.height = this._initialBodyHeightValue || ''
			this.sticky = false
		}
	}

	/**
	 * Adds listeners for Tab keyboard event to all focusable elements in global nav
	 */
	private _addTabListeners() {
		this._navFocusableElements.forEach(btn => {
			btn.addEventListener('keydown', e => {
				this._handleTabEvent(e)
				this._handleShiftTabEvent(e)
			})
		})
	}

	/**
	 * Add listener for Tab keyboard event to myAccount button in global nav
	 * The button is nested within a Shadow Root and is not picked up by '_addTabListeners()'
	 */
	private _addMyAccountTabListener() {
		const myAccount = <HTMLElement>this.shadowRoot.querySelector('gb-myaccount-nav')
		myAccount?.addEventListener('keydown', e => {
			this._handleMyAccountTabEvent(e)
		})
	}

	/**
	 * Add listener to first focusable element after the global nav
	 */
	private _addMainContentShiftTabListener() {
		const mainContent = this.appContentSelector
			? <HTMLElement>document.querySelector(this.appContentSelector)
			: <HTMLElement>document.querySelector('main')
		const { first } = getFirstAndLastFocusableElement(mainContent)
		first?.addEventListener('keydown', e => {
			this._handleMainContentShiftTabEvent(e)
		})
	}

	/**
	 * Finds next element for custom Tab order when in Small/Mediume Viewports
	 * New Hamburger menu styling requires custom tab order skiptomain>HamburgerMenu>Logo>MyAccount
	 * Also when HB menu is present a custom cycle is also required when tabbing into HB flyout.
	 */
	private _focusNextVisibleNavElement(e: KeyboardEvent) {
		const isHamburgerMenuEnabled =
			this.shadowRoot.querySelectorAll('.gn-main-nav button.gn-main-button[data-hamburger-menu="true"]').length ||
			this.shadowRoot.querySelectorAll('.gn-main-nav a.gn-main-link.stat-image-link[data-hamburger-menu="true"]')
				.length > 0
				? 'true'
				: 'false'
		const getMainNavLastFocusableElement = () => {
			const mainNav = this.shadowRoot.querySelector('.gn-main-nav')
			const last = <HTMLElement>mainNav.querySelector('.stacked-menu-container .gn-main-button')
			return last
		}

		const getHBMenuMainNavLastFocusableEl = () => {
			const mainNav = this.shadowRoot.querySelector('.gn-main-nav')
			const mainNavEls = mainNav.querySelectorAll('[class*="gn-main"]:not(.hide-large)')
			const last = mainNavEls[mainNavEls.length - 1]
			return last
		}

		const getMiscNavFirstFocusableElement = () => {
			const miscNav = this.shadowRoot.querySelector('.gn-misc')
			const { first } = getFirstAndLastFocusableElement(miscNav)
			return first
		}

		const getLogoFocusableElement = () => {
			const logoContent = this.shadowRoot.querySelector('.gn-logo-container')
			const { first } = getFirstAndLastFocusableElement(logoContent)
			return first
		}

		const currElement = this.shadowRoot.activeElement
		/**
		 **Custom Hamburger Menu Tab order Logic requires a specific order for SM/MD Viewports and LG Viewport
		 **The logic is only supposed to fire when IsHamburgerMenuEnabled is true.
		 */
		if (isHamburgerMenuEnabled === 'true') {
			const lastLinkMainNavEl = getHBMenuMainNavLastFocusableEl()
			const skipEl = this.shadowRoot.querySelector('.gb-skip-to-main-content')
			const logoEl = <HTMLElement>this.shadowRoot.querySelector('.gn-logo-container a')
			const miscNavEl = getMiscNavFirstFocusableElement()
			const hamburgerBtn = <HTMLElement>(
				this.shadowRoot.querySelector('.gn-main-nav button.gn-main-button[data-hamburger-menu="true"]')
			)
			const primaryLinkBtn1 = <HTMLElement>(
				this.shadowRoot.querySelector('.gn-main-nav button.gn-main-button[flyout="primary-1"]')
			)
			const primaryLinkBtn2 = <HTMLElement>(
				this.shadowRoot.querySelector('.gn-main-nav button.gn-main-button[flyout="primary-2"]')
			)

			let elementToFocus: HTMLElement

			if (currElement === skipEl) {
				elementToFocus = hamburgerBtn
			} else if (currElement === hamburgerBtn && (isSmallViewport() || isMediumViewport())) {
				elementToFocus = logoEl
			} else if (currElement === lastLinkMainNavEl && !isSmallViewport() && !isMediumViewport()) {
				elementToFocus = logoEl
			} else if (currElement === lastLinkMainNavEl && (isSmallViewport() || isMediumViewport())) {
				elementToFocus = <HTMLElement>this.nextElementSibling
			} else if (currElement === logoEl) {
				elementToFocus = miscNavEl
			} else if (currElement === miscNavEl && (isSmallViewport() || isMediumViewport())) {
				elementToFocus = primaryLinkBtn1
			} else if (currElement === primaryLinkBtn1 && (isSmallViewport() || isMediumViewport())) {
				elementToFocus = primaryLinkBtn2
			}

			if (elementToFocus) {
				elementToFocus.focus()
				e.preventDefault()
			}
		}
		/*
		 *If HB Menu is present we skip original logic below
		 */

		if (isSmallViewport() || isMediumViewport()) {
			if (isHamburgerMenuEnabled !== 'true') {
				if (currElement === getLogoFocusableElement()) {
					const elToFocus = getMainNavLastFocusableElement()
					elToFocus.focus()
				} else if (currElement === getMainNavLastFocusableElement()) {
					const elToFocus = <HTMLElement>this.nextElementSibling
					elToFocus.focus()
				}
			}
		}
	}

	/**
	 * Finds next element for custom Tab order when on the myAccount element
	 */
	private _focusFromMyAccountElement(e: KeyboardEvent) {
		const isHamburgerMenuEnabled =
			this.shadowRoot.querySelectorAll('.gn-main-nav button.gn-main-button[data-hamburger-menu="true"]').length ||
			this.shadowRoot.querySelectorAll('.gn-main-nav a.gn-main-link.stat-image-link[data-hamburger-menu="true"]')
				.length > 0
				? 'true'
				: 'false'
		const getLogoFocusableElement = () => {
			const logoContent = this.shadowRoot.querySelector('.gn-logo-container')
			const { first } = getFirstAndLastFocusableElement(logoContent)
			return first
		}

		if ((isSmallViewport() || isMediumViewport()) && isHamburgerMenuEnabled !== 'true') {
			if (this._myAccountNav.flyout !== 'open') {
				const elToFocus = getLogoFocusableElement()
				elToFocus.focus()
			}
		} else if (
			this.shadowRoot
				.querySelectorAll('.gn-main-nav button.gn-main-button[flyout="primary-1"]')[0]
				.classList.contains('hide-small') ||
			this.shadowRoot
				.querySelectorAll('.gn-main-nav button.gn-main-button[flyout="primary-1"]')[0]
				.classList.contains('hide-medium')
		) {
			const bodyEl = <HTMLElement>this.nextElementSibling
			bodyEl.focus()
			e.preventDefault()
		}
	}

	/**
	 * Finds next element for custom Shift+Tab order when in Small/Medium Viewports
	 */
	private _focusPreviousVisibleElement(e: KeyboardEvent) {
		const isHamburgerMenuEnabled =
			this.shadowRoot.querySelectorAll('.gn-main-nav button.gn-main-button[data-hamburger-menu="true"]').length ||
			this.shadowRoot.querySelectorAll('.gn-main-nav a.gn-main-link.stat-image-link[data-hamburger-menu="true"]')
				.length > 0
				? 'true'
				: 'false'
		const getMainNavFirstFocusableElement = () => {
			const mainNav = this.shadowRoot.querySelector('.gn-main-nav')
			const first = <HTMLElement>mainNav.firstElementChild
			return first
		}

		const getMiscNavFirstFocusableElement = () => {
			const miscNav = this.shadowRoot.querySelector('.gn-misc')
			const { first } = getFirstAndLastFocusableElement(miscNav)
			return first
		}

		const getMyAccountFocusableElement = () => {
			const mainContent = <HTMLElement>document.querySelector('main')
			const { first } = getFirstAndLastFocusableElement(mainContent)
			return first
		}

		const getHBMenuMainNavLastFocusableEl = () => {
			const mainNav = this.shadowRoot.querySelector('.gn-main-nav')
			const mainNavEls = mainNav.querySelectorAll('[class*="gn-main"]:not(.hide-large)')
			const last = <HTMLElement>mainNavEls[mainNavEls.length - 1]
			return last
		}

		const currElement = this.shadowRoot.activeElement

		if (isHamburgerMenuEnabled === 'true') {
			const logoEl = <HTMLElement>this.shadowRoot.querySelector('.gn-logo-container a')
			const skipToMainContentBtn = <HTMLElement>this.shadowRoot.querySelector('.gb-skip-to-main-content')
			const myaccEl = getMiscNavFirstFocusableElement()
			const lastLinkMainNavEl = getHBMenuMainNavLastFocusableEl()
			const hamburgerBtn = <HTMLElement>(
				this.shadowRoot.querySelector('.gn-main-nav button.gn-main-button[data-hamburger-menu="true"]')
			)
			const primaryLinkBtn1 = <HTMLElement>(
				this.shadowRoot.querySelector('.gn-main-nav button.gn-main-button[flyout="primary-1"]')
			)
			const primaryLinkBtn2 = <HTMLElement>(
				this.shadowRoot.querySelector('.gn-main-nav button.gn-main-button[flyout="primary-2"]')
			)
			let elementToFocus: HTMLElement

			if (isSmallViewport() || isMediumViewport()) {
				if (currElement === logoEl) {
					elementToFocus = hamburgerBtn
				} else if (currElement === myaccEl) {
					elementToFocus = logoEl
				} else if (currElement === hamburgerBtn) {
					elementToFocus = skipToMainContentBtn
				} else if (currElement === primaryLinkBtn1) {
					elementToFocus = myaccEl
				} else if (currElement === primaryLinkBtn2) {
					elementToFocus = primaryLinkBtn1
				}
			} else {
				if (currElement === logoEl) {
					elementToFocus = lastLinkMainNavEl //needs to be first link on larg
				} else if (currElement === myaccEl) {
					elementToFocus = logoEl
				} else if (currElement === hamburgerBtn) {
					elementToFocus = skipToMainContentBtn
				}
			}

			if (elementToFocus) {
				elementToFocus.focus()
				e.preventDefault()
			}
		}

		if (isSmallViewport() || isMediumViewport()) {
			if (isHamburgerMenuEnabled !== 'true') {
				if (currElement === getMainNavFirstFocusableElement()) {
					const elToFocus = getMyAccountFocusableElement()
					elToFocus.focus()
				} else if (currElement === getMiscNavFirstFocusableElement()) {
					const elToFocus = getMainNavFirstFocusableElement()
					elToFocus.focus()
				}
			}
		}
	}

	/**
	 * Finds element to focus on in global nav when re-entering on Shift+Tab
	 */
	private _focusFromMainContentElement() {
		const getMiscNavFirstFocusableElement = () => {
			const miscNav = this.shadowRoot.querySelector('.gn-misc')
			const { first } = getFirstAndLastFocusableElement(miscNav)
			return first
		}

		if (isSmallViewport() || isMediumViewport()) {
			const elToFocus = getMiscNavFirstFocusableElement()
			elToFocus.focus()
		}
	}

	/**
	 * Handle Tab Listeners for all non-myAccount global nav elements
	 */
	private _handleTabEvent(e: KeyboardEvent) {
		if (isTabOnly(e)) {
			this._focusNextVisibleNavElement(e)
		}
	}

	/**
	 * Handle Tab Listener for myAccount button
	 */
	private _handleMyAccountTabEvent(e: KeyboardEvent) {
		if (isTabOnly(e)) {
			this._focusFromMyAccountElement(e)
		}
	}

	/**
	 * Handle Shift+Tab Listeners for all non-myAccount global nav elements
	 */
	private _handleShiftTabEvent(e: KeyboardEvent) {
		if (isShiftTab(e)) {
			this._focusPreviousVisibleElement(e)
		}
	}

	/**
	 * Handle Shift+Tab Listener for first element outside of global nav
	 */
	private _handleMainContentShiftTabEvent(e: KeyboardEvent) {
		if (isShiftTab(e)) {
			this._focusFromMainContentElement()
		}
	}

	/* private getAuthStateElements(attribute: 'show-auth' | 'show-unauth') {
		return Array.from(this.shadowRoot.querySelectorAll(`[${attribute}]`)).map((e: HTMLElement) => {
			return { el: e, initialDisplay: e.style.display }
		})
	} */

	/**
	 * Loads external CSS by automatically parsing base URL override for brand/region/locale/language
	 */
	public async _loadExternalColors(): Promise<void> {
		// First check if a custom colors link tag already exists in the head
		let cssUrl = this._findExistingCustomColorsLink()

		// If no existing link, build the URL dynamically
		if (!cssUrl) {
			cssUrl = this._buildDynamicColorUrl()
		}

		if (!cssUrl) {
			// Specific warnings already logged in helper methods
			return
		}

		try {
			const response = await fetch(cssUrl)
			if (!response.ok) {
				console.warn(`Custom colors failed to load: HTTP ${response.status} when fetching ${cssUrl}`)
				return
			}

			const cssText = await response.text()

			// Inject styles into the shadow DOM for proper encapsulation
			const styleElement = document.createElement('style')
			styleElement.textContent = cssText
			styleElement.setAttribute('data-source', 'custom-colors')
			this.shadowRoot?.appendChild(styleElement)
		} catch (error) {
			console.warn(`Custom colors failed to load: Network error when fetching CSS`, error)
		}
	}

	/**
	 * Checks if a custom colors link tag already exists in the document head
	 * Returns the href if found, null otherwise
	 */
	public _findExistingCustomColorsLink(): string | null {
		// Look for link tags in the head that contain custom colors CSS
		const linkElements = document.head.querySelectorAll('link[rel="stylesheet"]')

		for (const link of linkElements) {
			const href = link.getAttribute('href')
			if (href && href.includes('content/gm/api/services/customcolor.')) {
				// Ensure the href starts with / if it doesn't already
				return href.startsWith('/') ? href : `/${href}`
			}
		}

		return null
	}

	/**
	 * Builds dynamic color CSS URL based on base URL override
	 * Format: /content/gm/api/services/customcolor.{brand}.{region}.{locale}.{language}.css
	 */
	public _buildDynamicColorUrl(): string | null {
		const urlInfo = this._parseBaseUrlOverride()
		if (!urlInfo) return null

		return `/content/gm/api/services/customcolor.${urlInfo.brand}.${urlInfo.region}.${urlInfo.locale}.${urlInfo.language}.css`
	}

	/**
	 * Parses the base URL override to extract brand, region, locale, and language information
	 * Expected format: /content/experience-fragments/{brand}/{region}/{locale}/{language}/...
	 */
	public _parseBaseUrlOverride(): { brand: string; region: string; locale: string; language: string } | null {
		try {
			if (!this._baseUrlOverride) {
				console.warn('Custom colors failed to load: No base URL override provided')
				return null
			}

			// Convert to string if it's not already
			const baseUrl =
				typeof this._baseUrlOverride === 'string' ? this._baseUrlOverride : String(this._baseUrlOverride)

			// Find the consistent part and extract what comes after
			const experienceFragmentsPath = '/content/experience-fragments/'
			const fragmentIndex = baseUrl.indexOf(experienceFragmentsPath)

			if (fragmentIndex === -1) {
				console.warn(
					'Custom colors failed to load: Base URL does not contain expected experience fragments path'
				)
				return null
			}

			// Extract the path after /content/experience-fragments/
			const pathAfterFragments = baseUrl.substring(fragmentIndex + experienceFragmentsPath.length)
			const pathParts = pathAfterFragments.split('/')

			if (pathParts.length < 4) {
				console.warn(
					'Custom colors failed to load: Base URL does not contain enough path segments for brand/region/locale/language'
				)
				return null
			}

			const brand = pathParts[0]
			const region = pathParts[1]
			const locale = pathParts[2]
			const language = pathParts[3]

			if (!brand || !region || !locale || !language) {
				console.warn('Custom colors failed to load: One or more required path segments are empty')
				return null
			}

			return { brand, region, locale, language }
		} catch (error) {
			console.warn('Custom colors failed to load: Error parsing base URL override', error)
			return null
		}
	}

	private _initAuthState() {
		// this.authElements = this.getAuthStateElements(this.showWhenAuthAttribute)
		// this.unauthElements = this.getAuthStateElements(this.showWhenUnauthAttribute)

		if (window.gbpe) {
			this._renderAuthState()
		} else {
			window.addEventListener(PortableExperiencesEvents.loaded, () => this._renderAuthState())
		}
	}

	private async _renderAuthState() {
		await window.gbpe.auth.loaded
		/*
		if (window.gbpe.auth.currentAuthState === 1) {
			this.toggleAuthStateElements(this.authElements, 'show')
			this.toggleAuthStateElements(this.unauthElements, 'hide')
		} else {
			this.toggleAuthStateElements(this.authElements, 'hide')
			this.toggleAuthStateElements(this.unauthElements, 'show')
		}
		*/
	}

	private _initFetchedContent(markup: string): HTMLElement {
		const rawContent = parseString(markup)
		const template = rawContent.querySelector('template')
		const content = template.content.cloneNode(true) as HTMLElement
		Array.from(content.children).forEach(c => this.shadowRoot.appendChild(c))
		this._initExtrasListeners()
		this._initWindowResizeListener()
		this._dispatchRendered(INITIAL_RENDER)
		return this._initSkipToMainContent()
	}

	private _initShadowDom(): void {
		this.attachShadow({ mode: 'open' })
		const stylesTag = document.createElement('style')
		stylesTag.textContent = this._styles
		this.shadowRoot.appendChild(stylesTag)
		this._addPrintStyles()
	}

	private _initExtrasListeners() {
		const extrasContainer = this.shadowRoot.querySelector('.gn-extras')

		document.addEventListener('keydown', e => {
			if (e.key === 'Tab') {
				extrasContainer.contains(document.activeElement)

				this._dispatchRendered(HEIGHT_RENDER)
			}
		})
	}

	private _initSkipToMainContent(): HTMLElement {
		const skipToMainContentBtn = this.shadowRoot.querySelector('.gb-skip-to-main-content')
		skipToMainContentBtn.classList.add('stat-button-link')
		skipToMainContentBtn.setAttribute('data-dtm', 'global nav')
		const mainContentHack = document.createElement('div')
		mainContentHack.tabIndex = -1
		mainContentHack.className = 'gb-main-content'

		skipToMainContentBtn.addEventListener('click', () => mainContentHack.focus())

		return mainContentHack
	}

	private _initStaticContent(): HTMLElement {
		const templateContents: HTMLTemplateElement = this.querySelector('template')
		const contents = templateContents.content.cloneNode(true)
		this.shadowRoot.appendChild(contents)
		templateContents.remove()
		this._initExtrasListeners()
		this._initWindowResizeListener()
		this._dispatchRendered(INITIAL_RENDER)

		return this._initSkipToMainContent()
	}

	private _initWindowResizeListener() {
		window.addEventListener('resize', debounce(this._handleResize.bind(this), 200))
	}

	private _handleResize() {
		this._closeFlyoutIfNotMediumViewport()
		this._dispatchRendered(HEIGHT_RENDER)
	}

	private _closeFlyoutIfNotMediumViewport() {
		if (!isMediumViewport() && this._isHorizontalWindowResize()) {
			this.flyoutService.closeOpenFlyout()
			// Just ensuring overlay closed properly
			if (window.gbpe.overlay.overlayShown) window.gbpe.overlay.toggleOverlay()
		}
	}

	private _isHorizontalWindowResize(): boolean {
		let isHorizontalWindowResize = false

		if (window.innerWidth !== this._windowInnerWidth) {
			this._windowInnerWidth = window.innerWidth
			isHorizontalWindowResize = true
		}

		return isHorizontalWindowResize
	}

	private async _renderDynamic(variation: string) {
		this._checkNavParent()

		const url = getNetStorageContentPath(variation, this._templateSuffix, this._baseUrlOverride)
		let html: string

		try {
			const response = await fetch(url)
			if (response.status === 404) throw new Error()
			// Need to check if the response is JSON, lower environment's currently return JSON from NetStorage
			const rawText = await response.text()
			const isJson = rawText.includes(`"status":`)
			html = isJson ? JSON.parse(rawText).html : rawText
		} catch {
			throw new Error(`The variation ${variation} does not exist at ${url}.`)
		}

		this._initShadowDom()
		const mainContentDiv = this._initFetchedContent(html)
		this.parentNode.insertBefore(mainContentDiv, this.nextSibling)
		this._initAuthState()
	}

	private async _renderStatic() {
		this._checkNavParent()

		this._initShadowDom()
		const mainContentDiv = this._initStaticContent()
		this.parentNode.insertBefore(mainContentDiv, this.nextSibling)

		this._initAuthState()
	}

	private _checkNavParent() {
		const navParent = this.parentNode
		if (navParent.nodeName.toLowerCase() !== 'body' && !STORYBOOK)
			throw new Error('gb-global-nav must be an immediate child of the body tag.')
	}

	private _addPrintStyles() {
		const printerStylesTag = document.createElement('style')
		printerStylesTag.textContent = this._printStyles
		document.head.appendChild(printerStylesTag)
	}

	private _toggleAuthStateElements(elements: AuthStateElement[], action: 'show' | 'hide') {
		elements.forEach(e => {
			e.el.style.display = action === 'show' ? e.initialDisplay : 'none'
		})
	}

	private _toggleHide() {
		if (this.hide) {
			this.classList.add('gb-gn-hiding')
			this.addEventListener(
				'animationend',
				() => {
					this.classList.add('gb-gn-hidden')
					this.classList.remove('gb-gn-hiding')
				},
				{
					once: true,
				}
			)
		} else {
			this.classList.remove('gb-gn-hidden')
			this.classList.add('gb-gn-showing')
			this.addEventListener(
				'animationend',
				() => {
					this.classList.remove('gb-gn-showing')
				},
				{
					once: true,
				}
			)
		}
	}

	private _toggleSticky() {
		if (this.sticky) {
			this.classList.add('gb-gn-sticky')
			this._dispatchRendered(NAV_STICKY)
		} else {
			this.classList.remove('gb-gn-sticky')
			this._dispatchRendered(NAV_UNSTICKY)
		}
	}

	//
	// Accessors
	//

	public get flyoutService(): FlyoutService {
		return this._flyoutService
	}

	//
	// Events
	//

	private _dispatchRendered(name: string) {
		const detail: RenderEvent = {
			element: this,
			height: this.offsetHeight,
		}

		this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }))
	}
}
